import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import StatsGrid from '@/components/StatsGrid';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Plus, FileText, Download, Send, Trash2,
  DollarSign, CheckCircle, Clock, Eye, Mail
} from 'lucide-react';


const statusColors = {
  draft: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800'
};

const Invoices = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [cases, setCases] = useState([]);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [caseBillingSummary, setCaseBillingSummary] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [invoicesRes, casesRes] = await Promise.all([
        api.get(`/invoices`),
        api.get(`/cases`)
      ]);
      setInvoices(invoicesRes.data);
      setCases(casesRes.data);
    } catch (error) {
      console.error('Failed to fetch data', error);
      toast.error('Failed to load data');
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (!['admin', 'senior_partner'].includes(user.role)) {
        toast.error('Access denied. Admin privileges required.');
        navigate('/');
        return;
      }
      fetchData();
    }
  }, [user, loading, navigate, fetchData]);

  const fetchCaseBilling = async (caseId) => {
    try {
      const response = await api.get(`/cases/${caseId}/billing-summary`);
      setCaseBillingSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch billing summary', error);
      setCaseBillingSummary(null);
    }
  };

  const generateInvoice = async () => {
    if (!selectedCaseId) {
      toast.error('Please select a case');
      return;
    }

    try {
      const response = await api.post(
        `/invoices/generate-from-case/${selectedCaseId}`,
        {}
      );
      toast.success(`Invoice ${response.data.invoice_number} created`);
      setShowGenerateDialog(false);
      setSelectedCaseId('');
      setCaseBillingSummary(null);
      fetchData();
    } catch (error) {
      console.error('Failed to generate invoice', error);
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  const downloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Failed to download PDF', error);
      toast.error('Failed to download PDF');
    }
  };

  const sendInvoice = async (invoice) => {
    if (!invoice.client_email) {
      const email = prompt('Enter client email address:');
      if (!email) return;
      
      // Update invoice with email first
      try {
        await api.put(`/invoices/${invoice.id}`, {
          ...invoice,
          client_email: email
        });
        invoice.client_email = email;
      } catch (error) {
        toast.error('Failed to update email');
        return;
      }
    }

    try {
      const response = await api.put(
        `/invoices/${invoice.id}/send`,
        {}
      );
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Failed to send invoice', error);
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    }
  };

  const markAsPaid = async (invoiceId) => {
    if (!confirm('Mark this invoice as paid?')) return;
    try {
      await api.put(
        `/invoices/${invoiceId}/mark-paid`,
        {}
      );
      toast.success('Invoice marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!confirm('Delete this invoice? Time entries will be reverted to approved status.')) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const viewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailDialog(true);
  };

  const stats = {
    total: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    totalAmount: invoices.reduce((sum, i) => sum + (i.total || 0), 0),
    paidAmount: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0)
  };

  // Cases with approved time entries
  const casesWithApproved = cases.filter(c => c.status !== 'closed');

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title="Invoices"
        titleTestId="invoices-title"
        actions={
          <Button
            onClick={() => {
              setSelectedCaseId('');
              setCaseBillingSummary(null);
              setShowGenerateDialog(true);
            }}
            data-testid="generate-invoice-btn"
          >
            <Plus size={20} /> Generate Invoice
          </Button>
        }
      />

      {/* Stats Cards */}
      <StatsGrid cols={4}>
        <StatCard icon={FileText} iconClassName="text-primary" value={stats.total} label="Total Invoices" />
        <StatCard icon={Clock} iconClassName="text-yellow-500" valueClassName="text-yellow-600" value={stats.draft + stats.sent} label="Outstanding" />
        <StatCard icon={CheckCircle} iconClassName="text-green-500" valueClassName="text-green-600" value={stats.paid} label="Paid" />
        <StatCard icon={DollarSign} valueClassName="text-accent" value={`$${stats.totalAmount.toLocaleString()}`} label="Total Billed" />
      </StatsGrid>

      {/* Invoices List */}
      <Tabs defaultValue="all" className="space-y-6">
          <TabsList>
            <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
            <TabsTrigger value="draft">Draft ({stats.draft})</TabsTrigger>
            <TabsTrigger value="sent">Sent ({stats.sent})</TabsTrigger>
            <TabsTrigger value="paid">Paid ({stats.paid})</TabsTrigger>
          </TabsList>

          {['all', 'draft', 'sent', 'paid'].map(tab => (
            <TabsContent key={tab} value={tab}>
              {invoices.filter(i => tab === 'all' || i.status === tab).length > 0 ? (
                <div className="space-y-4">
                  {invoices
                    .filter(i => tab === 'all' || i.status === tab)
                    .map((invoice) => (
                      <Card key={invoice.id} data-testid={`invoice-card-${invoice.id}`}>
                        <CardContent className="p-6">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-mono text-lg font-semibold text-primary">
                                  {invoice.invoice_number}
                                </span>
                                <Badge className={statusColors[invoice.status]}>
                                  {invoice.status}
                                </Badge>
                              </div>
                              <p className="font-sans text-sm font-medium mb-1">
                                {invoice.client_name}
                              </p>
                              <p className="font-sans text-sm text-muted-foreground">
                                Case: {invoice.case_number}
                              </p>
                              <p className="font-sans text-xs text-muted-foreground mt-1">
                                Created: {new Date(invoice.created_at).toLocaleDateString()}
                                {invoice.paid_at && ` • Paid: ${new Date(invoice.paid_at).toLocaleDateString()}`}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-serif text-3xl font-bold text-primary">
                                ${invoice.total?.toLocaleString()}
                              </p>
                              <p className="font-sans text-xs text-muted-foreground">
                                {invoice.line_items?.length || 0} items
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewInvoice(invoice)}
                              >
                                <Eye size={16} className="mr-1" /> View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => downloadPDF(invoice.id, invoice.invoice_number)}
                              >
                                <Download size={16} className="mr-1" /> PDF
                              </Button>
                              {invoice.status === 'draft' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => sendInvoice(invoice)}
                                  >
                                    <Send size={16} className="mr-1" /> Send
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => deleteInvoice(invoice.id)}
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </>
                              )}
                              {invoice.status === 'sent' && (
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => markAsPaid(invoice.id)}
                                >
                                  <CheckCircle size={16} className="mr-1" /> Mark Paid
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              ) : (
                <EmptyState
                  icon={FileText}
                  message={tab === 'all' ? 'No invoices yet' : `No ${tab} invoices`}
                />
              )}
            </TabsContent>
          ))}
        </Tabs>

      {/* Generate Invoice Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              Generate Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Select a case to generate an invoice from its approved time entries.
            </p>
            
            <div>
              <Label htmlFor="case-select">Select Case *</Label>
              <Select
                value={selectedCaseId}
                onValueChange={(value) => {
                  setSelectedCaseId(value);
                  if (value) fetchCaseBilling(value);
                }}
              >
                <SelectTrigger id="case-select" data-testid="case-select">
                  <SelectValue placeholder="Choose a case" />
                </SelectTrigger>
                <SelectContent>
                  {casesWithApproved.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.case_number} - {c.title} ({c.client})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {caseBillingSummary && (
              <div className="p-4 bg-secondary rounded-lg">
                <h4 className="font-medium mb-2">Billing Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p>Total Hours:</p>
                  <p className="text-right">{caseBillingSummary.total_hours?.toFixed(1) || 0}</p>
                  <p>Approved (available to invoice):</p>
                  <p className="text-right font-semibold text-green-600">
                    ${caseBillingSummary.approved_amount?.toLocaleString() || 0}
                  </p>
                  <p>Already Invoiced:</p>
                  <p className="text-right">${caseBillingSummary.invoiced_amount?.toLocaleString() || 0}</p>
                </div>
                {(caseBillingSummary.approved_amount || 0) === 0 && (
                  <p className="mt-2 text-sm text-orange-600">
                    No approved time entries available. Approve time entries first.
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowGenerateDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={generateInvoice}
                className="flex-1 bg-primary text-primary-foreground"
                disabled={!selectedCaseId || (caseBillingSummary?.approved_amount || 0) === 0}
                data-testid="create-invoice-btn"
              >
                Generate Invoice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">
              Invoice {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="mt-4 space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground">Bill To:</p>
                  <p className="font-medium">{selectedInvoice.client_name}</p>
                  {selectedInvoice.client_email && (
                    <p className="text-sm text-muted-foreground">{selectedInvoice.client_email}</p>
                  )}
                </div>
                <Badge className={statusColors[selectedInvoice.status]}>
                  {selectedInvoice.status}
                </Badge>
              </div>

              <div className="p-3 bg-secondary rounded-lg">
                <p className="text-sm"><strong>Case:</strong> {selectedInvoice.case_number}</p>
                <p className="text-sm"><strong>Created:</strong> {new Date(selectedInvoice.created_at).toLocaleDateString()}</p>
              </div>

              <div>
                <h4 className="font-medium mb-2">Line Items</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-primary text-primary-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left" scope="col">Description</th>
                        <th className="px-3 py-2 text-right" scope="col">Rate</th>
                        <th className="px-3 py-2 text-right" scope="col">Hours</th>
                        <th className="px-3 py-2 text-right" scope="col">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.line_items?.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2">{item.description}</td>
                          <td className="px-3 py-2 text-right">${item.rate?.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right">{item.hours?.toFixed(2) || '-'}</td>
                          <td className="px-3 py-2 text-right">${item.amount?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end">
                <div className="text-right">
                  <p className="text-sm">Subtotal: ${selectedInvoice.subtotal?.toLocaleString()}</p>
                  {selectedInvoice.tax_amount > 0 && (
                    <p className="text-sm">Tax: ${selectedInvoice.tax_amount?.toLocaleString()}</p>
                  )}
                  <p className="font-serif text-2xl font-bold text-primary mt-2">
                    Total: ${selectedInvoice.total?.toLocaleString()}
                  </p>
                </div>
              </div>

              {selectedInvoice.notes && (
                <div className="p-3 bg-secondary rounded-lg">
                  <p className="text-sm font-medium">Notes:</p>
                  <p className="text-sm text-muted-foreground">{selectedInvoice.notes}</p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => downloadPDF(selectedInvoice.id, selectedInvoice.invoice_number)}
                  className="flex-1"
                >
                  <Download size={16} className="mr-2" /> Download PDF
                </Button>
                {selectedInvoice.status !== 'paid' && (
                  <Button
                    onClick={() => {
                      sendInvoice(selectedInvoice);
                      setShowDetailDialog(false);
                    }}
                    className="flex-1"
                  >
                    <Mail size={16} className="mr-2" /> Send to Client
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default Invoices;
